/**
 * Document AI service — wraps `/api/programs/{programId}/document-ai/*`.
 *
 * Surface:
 *  - listExtractions: documents with their latest extraction state
 *  - getCurrent:      latest extraction for a single document
 *  - process:         trigger (or re-run) OCR + classification
 *  - update:          edit extracted fields (manual override)
 *  - approve:         lock the extraction as the source of truth
 */
import { apiClient } from '@/modules/shared/services/apiClient';
import type { ApiResponse } from '@/modules/shared/types';
import type {
  DocAiExtraction,
  DocAiList,
  DocAiListFilter,
  DocAiUpdatePayload,
  DocAiApprovePayload,
} from '../types/documentAi';

const base = (programId: string) => `/api/programs/${programId}/document-ai`;

export const documentAiService = {
  async listExtractions(programId: string, filter: DocAiListFilter = {}): Promise<DocAiList> {
    const params = new URLSearchParams();
    Object.entries(filter).forEach(([k, v]) => {
      if (v != null && v !== '') params.append(k, String(v));
    });
    const qs = params.toString();
    const res = await apiClient.get<ApiResponse<DocAiList>>(
      `${base(programId)}/extractions${qs ? `?${qs}` : ''}`,
    );
    return res.data.data ?? { items: [], totalCount: 0, page: 1, pageSize: 50 };
  },

  async getCurrent(programId: string, entityDocumentId: string): Promise<DocAiExtraction | null> {
    try {
      const res = await apiClient.get<ApiResponse<DocAiExtraction>>(
        `${base(programId)}/documents/${entityDocumentId}/extraction`,
      );
      return res.data.data ?? null;
    } catch (err: any) {
      if (err?.response?.status === 404) return null;
      throw err;
    }
  },

  async process(programId: string, entityDocumentId: string, forceReRun = false): Promise<DocAiExtraction> {
    const res = await apiClient.post<ApiResponse<DocAiExtraction>>(
      `${base(programId)}/documents/${entityDocumentId}/process?forceReRun=${forceReRun}`,
    );
    return res.data.data!;
  },

  async update(programId: string, extractionId: string, patch: DocAiUpdatePayload): Promise<DocAiExtraction> {
    const res = await apiClient.put<ApiResponse<DocAiExtraction>>(
      `${base(programId)}/extractions/${extractionId}`,
      patch,
    );
    return res.data.data!;
  },

  async approve(programId: string, extractionId: string, payload: DocAiApprovePayload = {}): Promise<DocAiExtraction> {
    const res = await apiClient.post<ApiResponse<DocAiExtraction>>(
      `${base(programId)}/extractions/${extractionId}/approve`,
      payload,
    );
    return res.data.data!;
  },
};
